import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { buildThemedHtmlDoc, isLightTheme } from 'utils';
import { useThemeModeContext } from 'contexts/theme-mode-context';

const useThemedHtmlPreview = (html = '', options = {}) => {
  const { themeMode: themeModeOverride, defaultHeight = 460, heightOffset = 24 } = options;
  const { themeMode: contextThemeMode = 'light' } = useThemeModeContext();
  const themeMode = themeModeOverride || contextThemeMode;

  const iframeRef = useRef(null);
  const measuringRef = useRef(false);
  const [height, setHeight] = useState(defaultHeight);
  const [ready, setReady] = useState(false);

  const srcDoc = useMemo(() => buildThemedHtmlDoc(html, themeMode), [html, themeMode]);

  const fitToWidth = useCallback(() => {
    const iframe = iframeRef.current;

    if (measuringRef.current) return;

    try {
      const body = iframe?.contentDocument?.body;
      if (!body) return;

      measuringRef.current = true;

      body.style.transformOrigin = 'top left';
      body.style.transform = '';
      body.style.width = '';

      const containerWidth = iframe.clientWidth;
      const contentWidth = body.scrollWidth;
      const scale = contentWidth > containerWidth && containerWidth > 0 ? containerWidth / contentWidth : 1;

      if (scale < 1) {
        body.style.width = `${contentWidth}px`;
        body.style.transform = `scale(${scale})`;
      }

      setHeight(body.scrollHeight * scale + heightOffset);
      setReady(true);
    } catch {
    } finally {
      requestAnimationFrame(() => {
        measuringRef.current = false;
      });
    }
  }, [heightOffset]);

  const handleLoad = useCallback(() => {
    fitToWidth();

    const doc = iframeRef.current?.contentDocument;
    if (!doc?.body) return;

    doc.querySelectorAll('img').forEach((img) => {
      if (!img.complete) {
        img.addEventListener('load', fitToWidth, { once: true });
        img.addEventListener('error', fitToWidth, { once: true });
      }
    });

    doc.fonts?.ready?.then(fitToWidth).catch(() => {});
  }, [fitToWidth]);

  useEffect(() => {
    setReady(false);
  }, [srcDoc]);

  useEffect(() => {
    window.addEventListener('resize', fitToWidth);
    return () => window.removeEventListener('resize', fitToWidth);
  }, [fitToWidth]);

  const iframeProps = {
    ref: iframeRef,
    srcDoc,
    onLoad: handleLoad,
    sandbox: 'allow-same-origin',
    frameBorder: '0',
    scrolling: 'no',
    style: {
      display: 'block',
      width: '100%',
      height: ready ? height : defaultHeight,
      border: 'none',
      colorScheme: isLightTheme(themeMode) ? 'light' : 'dark',
      ...(ready ? {} : { position: 'absolute', top: 0, left: 0, visibility: 'hidden', pointerEvents: 'none' }),
    },
  };

  return { iframeRef, srcDoc, height, ready, fitToWidth, iframeProps, themeMode };
};

export default useThemedHtmlPreview;
